import { useRouter } from 'vue-router'

let refreshInFlightPromise = null
let refreshInFlightKey = null

/**
 * Generate a UUID v4 string
 * Uses crypto.randomUUID() if available (modern browsers), falls back to custom implementation
 * @returns {string} A UUID v4 string
 */
function generateUUID() {
  // Use built-in crypto.randomUUID() if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isUuid(value) {
  return typeof value === 'string' && UUID_REGEX.test(value)
}

/**
 * API client with centralized authentication handling
 * 
 * @param {string} endpoint - API endpoint path (without base URL)
 * @param {Object} options - Fetch request options
 * @param {Object} router - Vue router instance (optional, can be passed when used outside of components)
 * @param {Object} config - Additional configuration
 * @param {boolean} config.skipAuthCheck - Skip the 401 auth check (for login page)
 * @returns {Promise<Object>} - JSON response data
 */
export async function api(endpoint, options = {}, router = null, config = {}) {
  const apiUrl = process.env.NODE_ENV === 'production' 
    ? process.env.VUE_APP_API_URL 
    : '/api'
  
  const url = `${apiUrl}${endpoint}`

  const getToken = () => {
    try {
      return localStorage.getItem('fic') || null
    } catch (_) {
      // Intentionally ignore storage access errors (private mode, etc.)
      return null
    }
  }

  const setToken = (token) => {
    try {
      if (token) {
        localStorage.setItem('fic', token)
      }
    } catch (_) {
      // Intentionally ignore storage write errors
    }
  }

  const clearToken = () => {
    try {
      localStorage.removeItem('fic')
    } catch (_) {
      // Intentionally ignore storage removal errors
    }
  }
  
  const token = getToken()

  // Generate a unique trace ID for this request
  const traceId = generateUUID()

  // Build request options; no cookies, attach Authorization if token exists
  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'http-request-id': traceId,
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {})
    }
  }

  async function doFetchOnce(targetUrl, fetchOptions) {
    const response = await fetch(targetUrl, fetchOptions)
    return response
  }

  async function performRefresh(currentToken, { useSsoRefresh = false } = {}) {
    const refreshKey = useSsoRefresh ? 'sso' : 'default'
    // Reuse in-flight refresh if present
    if (refreshInFlightPromise && refreshInFlightKey === refreshKey) {
      return refreshInFlightPromise
    }

    refreshInFlightKey = refreshKey
    refreshInFlightPromise = (async () => {
      const refreshEndpoint = useSsoRefresh
        ? '/auth/google/refresh-token'
        : '/refresh-token'

      const refreshResponse = await fetch(`${apiUrl}${refreshEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'http-request-id': generateUUID(),
          ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {})
        }
      })

      if (!refreshResponse.ok) {
        throw new Error('Refresh failed')
      }

      const refreshText = await refreshResponse.text()
      let refreshData = null
      if (refreshText) {
        try { refreshData = JSON.parse(refreshText) } catch (_) {
          // If parsing fails, treat as missing token
        }
      }

      const newToken = refreshData && (refreshData.accessToken || refreshData.token)
      if (!newToken) {
        throw new Error('No new token in refresh response')
      }

      setToken(newToken)
      return newToken
    })()

    try {
      const newToken = await refreshInFlightPromise
      return newToken
    } finally {
      // Always clear the in-flight promise after it settles so future refreshes can proceed
      refreshInFlightPromise = null
      refreshInFlightKey = null
    }
  }

  // Attempt token refresh and retry once upon 401
  async function tryRefreshAndRetry(originalResponse, { useSsoRefresh = false } = {}) {
    // Skip refresh logic if explicitly disabled or already retried
    if (config.skipAuthCheck || config._retried) {
      return originalResponse
    }

    const currentToken = getToken()
    if (!currentToken) {
      return originalResponse
    }

    try {
      const newToken = await performRefresh(currentToken, { useSsoRefresh })

      const retriedOptions = {
        ...requestOptions,
        headers: {
          ...requestOptions.headers,
          'Authorization': `Bearer ${newToken}`
        }
      }

      // Mark retried to avoid loops if still 401
      config._retried = true
      const retriedResponse = await doFetchOnce(url, retriedOptions)
      return retriedResponse
    } catch (e) {
      // If refresh fails, clear token and redirect to login
      clearToken()
      const routerInstance = router || useRouter()
      if (!config.skipAuthCheck) {
        if (typeof routerInstance.push === 'function') {
          routerInstance.push('/login')
        } else {
          window.location.href = '/login'
        }
      }
      return originalResponse
    }
  }

  let response = await doFetchOnce(url, requestOptions)

  // If unauthorized, try refresh flow
  if (response.status === 401) {
    const preferSsoRefresh = await (async () => {
      try {
        const bodyClone = response.clone()
        const json = await bodyClone.json()
        return json && json.sso === true
      } catch (_) {
        return false
      }
    })()
    response = await tryRefreshAndRetry(response, { useSsoRefresh: preferSsoRefresh })
  }
  
  // After potential retry, handle 401 Unauthorized responses (unless skipAuthCheck is true)
  if (response.status === 401 && !config.skipAuthCheck) {
    const routerInstance = router || useRouter()
    if (typeof routerInstance.push === 'function') {
      routerInstance.push('/login')
    } else {
      window.location.href = '/login'
    }
    throw new Error('Authentication required')
  }
  
  // Handle non-OK responses
  if (!response.ok) {
    // Try to parse the error response
    let errorData;
    try {
      // Only try to parse JSON if there's content
      if (response.status !== 204) {
        errorData = await response.json();
      }
    } catch (e) {
      // If parsing fails, use default error
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    // If we have error data with a code, throw a custom error
    if (errorData && errorData.code) {
      const error = new Error(errorData.error || `Error: ${errorData.code}`);
      error.code = errorData.code;
      error.status = response.status;
      error.responseData = errorData;
      throw error;
    }
    
    // Default error
    throw new Error(`API request failed with status ${response.status}`)
  }
  
  // Only parse JSON if there's content
  if (![204, 205, 304].includes(response.status)) {
    const text = await response.text();
    if (text) {
      return JSON.parse(text);
    }
  }
  return null
}

// Cache for current user data
let currentUserCache = null
let currentUserPromise = null

/**
 * Get current user information (cached)
 * @param {Object} router - Vue router instance (optional)
 * @param {boolean} forceRefresh - Force a fresh API call
 * @returns {Promise<Object>} - User data
 */
export async function getCurrentUser(router = null, forceRefresh = false) {
  if (forceRefresh) {
    currentUserCache = null
    currentUserPromise = null
  }
  
  if (currentUserCache) {
    return currentUserCache
  }
  
  if (currentUserPromise) {
    return currentUserPromise
  }
  
  currentUserPromise = api('/users/me', {}, router).then(data => {
    currentUserCache = data
    currentUserPromise = null
    return data
  }).catch(err => {
    currentUserPromise = null
    throw err
  })
  
  return currentUserPromise
}

/**
 * Clear the current user cache (call on logout)
 */
export function clearCurrentUserCache() {
  currentUserCache = null
  currentUserPromise = null
} 